/**
 * 斐波SDK公用组件
 * @author wangxuejin
 * @since 2021-3-6
 */

import loadScript from '@fe/common/lib/load-script';
import { PAGE_TDK_TYPE } from '../constants/fibosdk';
import { getDeviceInfo } from '@fe/common/lib/device';
import { IS_FIRST_VISIT, IS_REGISTER } from 'constants/localStorage';

// 斐波新SDK脚本链接，优先从配置读取，没有则使用生产环境的脚本
const RPCONFIG = window.RPCONFIG;
const FIBO_SCRIPT =
  (RPCONFIG && RPCONFIG.sdk && RPCONFIG.sdk.fibo) ||
  'https://res.fibodata.com/data/fibosdk-v2.min.js';

export type InitCallback = () => void;

class FiboSDK {
  // 斐波SDK对象
  sdk: Record<string, any> | null = null;
  constructor({ initCallback }: { initCallback: InitCallback }) {
    // 加载SDK脚本
    this.loadSDK(initCallback);
  }

  /**
   * 加载斐波SDK脚本
   * @memberof FiboSDK
   */
  async loadSDK(initCallback: InitCallback) {
    const callback = async () => {
      // 新SDK的导出名称为 `fiboSDK`
      this.sdk = (window as any).fiboSDK;
      if (this.sdk) {
        const initData = getFiboInitData();

        let data;
        if (this.sdk.init) {
          try {
            data = await this.sdk.init({ ...initData });
          } catch (e) {
            console.error(e);
          }
          initCallback();
        }
        return data;
      }
    };
    // 加载SDK脚本
    if (!window.fiboSDK) {
      await loadScript(FIBO_SCRIPT);
    }
    // 执行回调
    return callback();
  }
}

let fiboSDK: FiboSDK | null = null;

/**
 * 初始化
 *
 * @export
 * @returns {FiboSDK} FiboSDK实例对象
 */
export function init(initCallback: InitCallback): FiboSDK {
  if (!fiboSDK) {
    fiboSDK = new FiboSDK({ initCallback });
  }
  return fiboSDK;
}

/**
 * 获取fibo类SDK初始化数据
 * 斐波接入文档: http://docs.tuzhanai.com/display/fibodata/H5-SDK
 * 官网埋点需求文档: http://wiki.tuzhanai.com/pages/viewpage.action?pageId=43333294
 */
export function getFiboInitData() {
  let identityValue;
  // 用户信息字段
  let userInfo = {
    nickname: '',
    headimgurl: '',
    sex: 0,
    city: '',
  };

  if (window.userInfo) {
    const { userId, userName, userPhoto, userCity } = window.userInfo;
    identityValue = userId;
    userInfo = {
      nickname: userName,
      headimgurl: userPhoto,
      sex: 0,
      city: userCity,
    };
  }

  const pfid = (window.RPCONFIG && window.RPCONFIG.sdk.pfid) || 'mk_7InrMAIZ0';
  const platform =
    (window.RPCONFIG && window.RPCONFIG.sdk.platform) ||
    'PrKAVWbUGUv3YvGeW76Ohim_oc8';

  let initData: Record<string, any> = {
    appid: 'tuzhanai',
    apptype: 'xapp',
    pfid,
    platform,
    setCustomData: { field: 'c_visitid' },
    userInfo,
  };
  // 确保存在用户登录后 用户信息添加到客户外部身份信息
  if (identityValue) {
    const identitys = [{ identityType: 'tuzhan_website', identityValue }];
    initData = Object.assign(initData, { identitys });
  }
  return initData;
}

/**
 * 推送自定义事件
 * @param eventType 事件类型
 * @param evetArgs 事件属性
 */
export function pushCustomEvent(
  eventType: string,
  evetArgs: Record<string, any>
) {
  if (!fiboSDK) return;
  if (fiboSDK.sdk && fiboSDK.sdk.pushCustomEvent) {
    const commonAttrs = addCommonAttribute();
    fiboSDK.sdk.pushCustomEvent(eventType, {
      ...evetArgs,
      ...commonAttrs,
    });
  }
}

/**
 * 浏览器版本
 */
const getBrowserInfo = () => {
  return {
    os: getDeviceInfo().os.name + ' ' + getDeviceInfo().os.version,
    browserVersion:
      getDeviceInfo().browser.name + ' ' + getDeviceInfo().browser.version,
  };
};

/**
 * 添加公有属性
 */
function addCommonAttribute() {
  let commonAttrs = {};
  const time = new Date().getTime();
  const c_isLogin = window.userInfo ? true : false;
  const audid = window.localStorage.getItem('audid') || '';
  const distinctid = window.localStorage.getItem('distinctid') || '';
  const screen = `${window.screen.height}x${window.screen.width}`;
  const useragent =
    (window && window.navigator && window.navigator.userAgent) || '';
  const c_path = window.location.pathname;
  const c_pageid = getPageInfo().type;
  const browserVer = getBrowserInfo().browserVersion;
  const uaVer = getBrowserInfo().os;
  commonAttrs = {
    appid: 'tuzhanai',
    apptype: 'website',
    // 访客id 访客的身份标记，即原来的viewid
    distinctid,
    // 客户档案id
    audid,
    // 访问时，如：2020-08-21 10:23:23
    time,
    // 访客的登录状态——0：未登录，1：已登录
    c_isLogin,
    // 操作系统
    useragent,
    // 操作系统版本
    uaVer,
    // 屏幕分辨率
    screen,
    // 浏览器型号
    c_browser: browserVer,
    // 浏览器版本
    c_browserVer: browserVer,
    // 页面链接
    url: window.location.href,
    // 页面域名
    c_domain: document.domain,
    // 页面路径
    c_path,
    // 页面id
    c_pageid,
    c_isFirstvisit: localStorage.getItem(IS_FIRST_VISIT) !== '0',
    c_isRegister: localStorage.getItem(IS_REGISTER) === '1',
  };
  return commonAttrs;
}

/**
 *  获取页面信息
 * @param pathname {string} 页面路由
 */
export function getPageInfo(pathname: string = window.location.pathname) {
  switch (true) {
    case /\/template\/showpage/g.test(pathname):
      return PAGE_TDK_TYPE.SHOWPAGE;
    case /\/template\/multipage/g.test(pathname):
      return PAGE_TDK_TYPE.MULTIPAGE;
    case /\/template\/longpage/g.test(pathname):
      return PAGE_TDK_TYPE.LONGPAGE;
    case /\/template\/shortvideo/g.test(pathname):
      return PAGE_TDK_TYPE.SHORTVIDEO;
    case /\/template\/vip/g.test(pathname):
      return PAGE_TDK_TYPE.TEMPLATE_VIP;
    case /\/template\/enterprise/g.test(pathname):
      return PAGE_TDK_TYPE.TEMPLATE_ENTERPRISE;
    case /\/template\/mytpl/g.test(pathname):
      return PAGE_TDK_TYPE.TEMPLATE_MYTPL;
    case /\/template\/search/g.test(pathname):
      return PAGE_TDK_TYPE.TEMPLATE_SEARCH;
    case /\/template\/topic/g.test(pathname):
      return PAGE_TDK_TYPE.TEMPLATE_TOPIC;
    case /\/template/g.test(pathname):
      return PAGE_TDK_TYPE.TEMPLATE;
    case /\/chajian/g.test(pathname):
      return PAGE_TDK_TYPE.CHAJIAN;
    default:
      return PAGE_TDK_TYPE.HOME;
  }
}
